import {
  AfterViewInit,
  Directive,
  ElementRef,
  HostListener,
  OnDestroy,
  Renderer2,
} from "@angular/core";
import { debounceTime, map, takeUntil } from "rxjs/operators";
import { BehaviorSubject, merge, Subject } from "rxjs";
import { LangChangeEvent, TranslateService } from "@ngx-translate/core";

/**
 * This directive adds the class .ellipsis to an element and a title attribute...
 * ...containing its innerText if not enough space is available
 * Title will be added when the UI language was changed or the window was resized
 *
 * Hints:
 * -Putting the directive on an element with display: flex; doesn't work --> Use block/inline-block
 * -A child element inside a flex-container needs to have a width/max-width property
 * -Parent container needs a width/max-width property OR if the parent has no width limitation, the element itself needs one
 * -Putting the directive on a div (child) which is inside another div (parent), works if one of them has a width/max-width
 * -Putting the directive on a span (child) which is inside a div (parent), works if one of them has a width/max-width property AND the span needs to be display:block/inline-block
 * -Works with multineline text because we're using white-space: nowrap;
 */
@Directive({
  selector: "[ellipsisTitle]",
  standalone: true,
})
export class EllipsisTitleDirective implements AfterViewInit, OnDestroy {
  // Not using asObservable() here because windowResized$ is only called inside this directive
  private windowResized$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(true);
  private ngUnsubscribe: Subject<void> = new Subject<void>();

  constructor(
    private elementRef: ElementRef,
    private renderer2: Renderer2,
    private translateService: TranslateService,
  ) {
    this.observeLanguageChangeAndResizeEvents();
  }

  public ngAfterViewInit(): void {
    this.renderer2.addClass(this.elementRef.nativeElement, "ellipsis");
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * Add a title attribute but only for elements that are truncated
   * Non-truncated text won't have a title
   */
  private addTitleWhenTextTruncated(): void {
    const element: HTMLElement = this.elementRef.nativeElement;
    const isTextTruncated: boolean = element.offsetWidth < element.scrollWidth;
    element.title = isTextTruncated ? element.innerText : "";
  }

  /**
   * Listener function for language change events and window resize events
   */
  private observeLanguageChangeAndResizeEvents(): void {
    merge(
      this.translateService.onLangChange.pipe(
        map((event: LangChangeEvent) => !!event.lang),
      ),
      this.windowResized$,
    )
      .pipe(debounceTime(500), takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.addTitleWhenTextTruncated();
      });
  }

  /**
   * Eventlistener for resize events
   */
  @HostListener("window:resize", ["$event"])
  private windowResized(): void {
    this.windowResized$.next(true);
  }
}
